const BigNumber = require('bignumber.js');
const PriceAdjustmentCalculator = require('../PriceAdjustmentCalculator');
const { MODAL_NAMES__EPOS } = require('@/epos/vue/modals/MODAL_NAMES__EPOS');

/**
 * Some useful info on Gift Aid:
 * https://www.visitengland.com/sites/default/files/rules_on_gift_aid_for_charitable_attractions_updated_version_jan_15.pdf
 *
 * There are 2 scenarios:
 *
 * 1) Customer opts to make a donation of at least 10% of the ticket price. The total amount of the entry cost + the
 * extra donation is then treated as a donation for Gift Aid purposes, not just the additional 10%. For DT purposes
 * the extra 10% goes into the 'donation' field.
 *
 * 2) The entire ticket price is considered a donation but the ticket must allow the customer free (or reduced rate)
 * for a 12 month period. For DT purposes the 'donation' field remains as zero.
 *
 * @constructor
 *
 * @param $timeout
 * @param {CurrentDevice} currentDevice
 * @param {CustomerScreenDataService} customerScreenDataService
 * @param {LangService} langService
 * @param {ModalFactory} modalFactory
 * @param {ToastFactory} toastFactory
 * @param {UserService} userService
 * @param {VueModalFactory} vueModalFactory
 */
const GiftAidPrompter = function (
    $timeout,
    currentDevice,
    customerScreenDataService,
    langService,
    modalFactory,
    toastFactory,
    userService,
    vueModalFactory
) {
    this.$timeout = $timeout;
    this.currentDevice = currentDevice;
    this.customerScreenDataService = customerScreenDataService;
    this.langService = langService;
    this.modalFactory = modalFactory;
    this.toastFactory = toastFactory;
    this.userSerice = userService;
    this.vueModalFactory = vueModalFactory;
};

GiftAidPrompter.prototype = {
    /**
     * Calculate the total additional donation needed to enable Gift Aid for every item in the cart.
     * This is the amount that non-zero gift aid rates add on to items.
     *
     * @param {Cart} cart
     *
     * @return {number}
     */
    calculateAdditionalGiftAidDonationForCart(cart) {
        const priceAdjustmentCalculator = new PriceAdjustmentCalculator();
        let totalIncrease = new BigNumber(0.00);

        cart.itemList.forEach((i) => {
            if (!i.item.giftAidRate) {
                return;
            }

            // Sum up the *increase in price required for Gift Aid*.
            let { difference } = priceAdjustmentCalculator.calculateAdjustedPrice(
                i.item.giftAidRate,
                Number(i.getDiscountedPrice())
            );

            let totalIncreaseForLine = new BigNumber(difference).times(i.quantity);

            totalIncrease = totalIncrease.plus(totalIncreaseForLine);
        });

        return totalIncrease.toNumber();
    },

    /**
     * Calculate the total donation for Gift Aidable items in the cart for the declaration.
     *
     * This is:
     * - The full item price if it has a zero increase gift aid rate.
     * - The full item price plus the increase for gift aid if it has a non-zero gift aid rate.
     *
     * It is different from the calculateAdditionalGiftAidDonationForCart, which is just how much the customer needs to
     * donate in addition to the item prices to enable Gift Aid. This is the total amount Gift Aid considers a
     * "donation", which includes the original price of the item.
     *
     * @param {Cart} cart
     *
     * @return {number}
     */
    calculateTotalGiftAidDonationForCart(cart) {
        const priceAdjustmentCalculator = new PriceAdjustmentCalculator();
        let totalDonation = new BigNumber(0.00);

        cart.itemList.forEach((i) => {
            if (!i.item.giftAidRate) {
                return;
            }

            // Sum up the *total item price including any increase required for Gift Aid*.
            let { newPrice } = priceAdjustmentCalculator.calculateAdjustedPrice(
                i.item.giftAidRate,
                Number(i.getDiscountedPrice())
            );

            let totalIncreasedPriceForLine = new BigNumber(newPrice).times(i.quantity);

            totalDonation = totalDonation.plus(totalIncreasedPriceForLine);
        });

        return totalDonation.toNumber();
    },

    /**
     * Asks the Gift Aid question and enables / disables Gift Aid on the cart as appropriate.
     *
     * @param {CartService} cartService
     *
     * @return {Promise<any>}
     */
    prompt(cartService) {
        // Return a new promise instead of just returning this.askQuestion so that we can use $timeout
        // in the 'then'. Without this the timeout would not have happened yet when the promise resolves.
        return new Promise((resolve, reject) => {
            this.askQuestion(cartService.currentCart)
                .then((result) => {
                    const { giftAid, giftAidPrices } = result;
                    this.$timeout(() => {
                        if (giftAid && giftAidPrices) {
                            cartService.enableGiftAid();
                        } else if (giftAidPrices) {
                            cartService.enableGiftAidPricesOnly();
                        } else {
                            cartService.disableGiftAid();
                        }

                        resolve(result);
                    });
                })
                .catch(reject);
        });
    },

    /**
     * @private
     *
     * Asks the Gift Aid question either on the customer screen or to the operator.
     * Returns a promise which resolves with a boolean for the answer.
     *
     * @param {Cart} cart
     *
     * @return {Promise<{giftAid: boolean, giftAidPrices: boolean}>}
     */
    askQuestion(cart) {
        const additionalDonation = this.calculateAdditionalGiftAidDonationForCart(cart);
        const totalDonation = this.calculateTotalGiftAidDonationForCart(cart);

        return this.vueModalFactory.open(
            MODAL_NAMES__EPOS.GIFT_AID,
            {
                additionalDonation,
                totalDonation,
            }
        );
    },

    getCustomerDeclarationHtml(additionalDonation, totalDonation) {
        const declaration = this.langService.getText(
            'GIFT_AID_MODAL.DECLARATION',
            {
                companyName: this.userSerice.getCompany().name,
                totalDonation,
            }
        );

        let question;
        if (additionalDonation) {
            question = this.langService.getText('GIFT_AID_MODAL.QUESTION_CUSTOMER_WITH_DONATION', { additionalDonation });
        } else {
            question = this.langService.getText('GIFT_AID_MODAL.QUESTION_CUSTOMER');
        }

        return '<img src="/epos/img/gift-aid-logo.png" alt="Gift Aid It" class="gift-aid-logo" />'
            + '<p>' + question + '</p>'
            + '<p class="description">' + declaration + '</p>';
    },
};

module.exports = GiftAidPrompter;
